/* The cells every surface on this screen shares.
 *
 * A score, a movement, a coverage figure and a target verdict each render in three places — the
 * ledger, the drawer's header, and the regression feed's context line. Three copies is how one of
 * them comes to render a `null` as a zero while the other two say "never scored", which is the one
 * defect `docs/design/SCORING.md` names as the product telling a lie. So they are components, and
 * this file is the only place they are drawn.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/shared/ui/StatusBadge';

import { formatRatio, formatScore, stalenessAge } from '../model/presentation';
import type { DeltaDisplay } from '../model/presentation';
import type { DatasetRow, Movement, QualityTarget } from '../model/types';

import { bandTone } from './tone';

/** The score, or the words that stand in for one.
 *
 * **Never a dash and never a zero.** An em dash in a numeric column reads as a failed load, and a
 * reader who thinks the number failed to arrive refreshes instead of investigating why nothing was
 * measured. Three absences, because "nobody has looked", "profiled and no rules written" and
 * "profiled, and here is what statistics alone say" are three different jobs.
 */
export function ScoreFigure({ row }: { readonly row: DatasetRow }): ReactElement {
  const { t } = useTranslation();

  if (row.value !== null) {
    return <span className="lq-scores__figure">{formatScore(row.value)}</span>;
  }
  const reading = row.unscored;
  if (reading?.kind === 'baseline') {
    return (
      <span className="lq-scores__absent" title={t('scoring.listing.baselineHint')}>
        {t('scoring.listing.baseline', { value: reading.value })}
      </span>
    );
  }
  return (
    <span className="lq-scores__absent">
      {reading?.kind === 'profiled'
        ? t('scoring.listing.profiledNotScored')
        : t('scoring.listing.neverScored')}
    </span>
  );
}

/** The band bar, with the target drawn on it where one exists.
 *
 * The tick is what makes the bar answer a question rather than restate the number: a 91 next to a
 * tick at 94 is visibly short in a way "91" is not. An absent score gets the hatched track, because
 * a zero-width fill and a measured zero are the same picture.
 */
export function ScoreBand({
  value,
  target,
  label,
}: {
  readonly value: number | null;
  readonly target: QualityTarget | null;
  readonly label: string;
}): ReactElement {
  const { t } = useTranslation();

  if (value === null) {
    return (
      <span
        className="lq-scores__track lq-scores__track--absent"
        role="img"
        aria-label={t('scoring.band.absent', { dataset: label })}
      />
    );
  }
  const tone = bandTone(value);
  return (
    <span
      className="lq-scores__track"
      role="img"
      aria-label={t('scoring.band.label', { dataset: label, value: formatScore(value) })}
    >
      <span
        className={`lq-scores__fill lq-scores__fill--${tone}`}
        style={{ width: `${String(Math.max(0, Math.min(100, value)))}%` }}
      />
      {target !== null && (
        <span
          className="lq-scores__tick"
          style={{ left: `${String(Math.max(0, Math.min(100, target.minScore)))}%` }}
          title={t('scoring.target.tick', {
            target: target.minScore,
            contract: target.contractName,
          })}
        />
      )}
    </span>
  );
}

/** Movement, or one of the four honest non-answers.
 *
 * Direction is a glyph as well as a hue, so it survives greyscale and a colour-blind reader. The
 * magnitude is always positive — the arrow carries the sign, so a caller cannot print a minus
 * beside a downward triangle.
 */
export function Delta({ delta }: { readonly delta: DeltaDisplay }): ReactElement {
  const { t } = useTranslation();

  if (delta.kind === 'up' || delta.kind === 'down') {
    const rising = delta.kind === 'up';
    return (
      <span className={rising ? 'lq-delta lq-delta--up' : 'lq-delta lq-delta--down'}>
        <span aria-hidden="true">{rising ? '▲' : '▼'}</span>{' '}
        {formatScore(delta.magnitude)}
        <span className="lq-visually-hidden">
          {rising ? t('scoring.delta.roseBy') : t('scoring.delta.fellBy')}
        </span>
      </span>
    );
  }
  if (delta.kind === 'flat') {
    return <span className="lq-muted">{t('scoring.delta.flat')}</span>;
  }
  if (delta.kind === 'incomparable') {
    return (
      <span className="lq-muted" title={t('scoring.delta.incomparableHint')}>
        {t('scoring.delta.incomparable')}
      </span>
    );
  }
  return (
    <span className="lq-muted" title={t('scoring.delta.noneHint')}>
      {t('scoring.delta.none')}
    </span>
  );
}

/** The movement cell, including the state where nothing has been read.
 *
 * **"Not read" is not "did not move", and collapsing them is the defect this cell exists to
 * avoid.** With no batch endpoint, a screen that left the cell blank for unread rows would put an
 * empty cell beside populated ones — and an empty cell in a movement column reads as stability,
 * which is a claim about the data made out of a limit of the request.
 */
export function MovementCell({
  movement,
  reading,
}: {
  readonly movement: Movement | undefined;
  /** True while the read for this row is in flight. Distinct from "not requested". */
  readonly reading: boolean;
}): ReactElement {
  const { t } = useTranslation();

  if (movement === undefined) {
    return (
      <span className="lq-muted" title={t('scoring.movement.unreadHint')}>
        {reading ? t('scoring.movement.reading') : t('scoring.movement.unread')}
      </span>
    );
  }
  return <Delta delta={movement.delta} />;
}

/** Coverage, always beside the score. A 98 over a twentieth of a table is a number about a
 * twentieth of a table, and there is no rendering here that omits it. */
export function CoverageCell({ row }: { readonly row: DatasetRow }): ReactElement {
  const { t } = useTranslation();

  if (row.summary === undefined)
    return <span className="lq-muted">{t('scoring.listing.noCoverage')}</span>;
  const percent = formatRatio(row.coverageRatio);
  if (percent === null)
    return <span className="lq-muted">{t('scoring.listing.noColumns')}</span>;
  return (
    <span>
      <span className="lq-scores__figure">
        {t('scoring.coverage.percent', { percent })}
      </span>
      <span className="lq-scores__sub">
        {t('scoring.coverage.columns', {
          covered: row.summary.columns_with_a_rule,
          total: row.summary.columns_total,
        })}
      </span>
    </span>
  );
}

/** The target verdict: met, short by N, or nothing promised.
 *
 * "No target" is muted words rather than a neutral badge, because a badge implies a state somebody
 * chose and no contract is the absence of a choice.
 */
export function TargetCell({ row }: { readonly row: DatasetRow }): ReactElement {
  const { t } = useTranslation();

  if (row.target === null) {
    return (
      <span className="lq-muted" title={t('scoring.target.noneHint')}>
        {t('scoring.target.none')}
      </span>
    );
  }
  if (row.slack === null) {
    return (
      <span className="lq-muted" title={t('scoring.target.unmeasuredHint')}>
        {t('scoring.target.unmeasured', { target: row.target.minScore })}
      </span>
    );
  }
  const met = row.slack >= 0;
  return (
    <span>
      <StatusBadge
        shape="square"
        tone={met ? 'ok' : row.status === 'critical' ? 'danger' : 'warn'}
        label={
          met
            ? t('scoring.target.met', { target: row.target.minScore })
            : t('scoring.target.short', { points: formatScore(Math.abs(row.slack)) })
        }
        title={t('scoring.target.tick', {
          target: row.target.minScore,
          contract: row.target.contractName,
        })}
      />
      <span className="lq-scores__sub">{row.target.contractName}</span>
    </span>
  );
}

/** How long ago the score was computed, and whether that is too long ago.
 *
 * Stale is its own word rather than a red number: an old 94 is not a bad score, it is a score
 * somebody should stop relying on, and those need different reactions.
 */
export function EvaluatedCell({ row }: { readonly row: DatasetRow }): ReactElement {
  const { t } = useTranslation();

  if (row.evaluatedAt === null) {
    return <span className="lq-muted">{t('scoring.evaluated.never')}</span>;
  }
  const age = stalenessAge(row.evaluatedAt, Date.now());
  if (age === null) {
    return <span className="lq-muted">{t('scoring.evaluated.unknown')}</span>;
  }
  return (
    <span>
      <span className={row.stale ? 'lq-scores__stale' : undefined}>
        {t('scoring.evaluated.ago', {
          age: t('scoring.age', { unit: age.unit, count: age.count }),
        })}
      </span>
      {row.stale && (
        <span className="lq-scores__sub" title={t('scoring.evaluated.staleHint')}>
          {t('scoring.evaluated.stale')}
        </span>
      )}
    </span>
  );
}

/** Owner, from the contract that named a producer. Never invented: a dataset with no contract has
 * no owner recorded anywhere in this platform, and saying so is the honest cell. */
export function OwnerCell({ row }: { readonly row: DatasetRow }): ReactElement {
  const { t } = useTranslation();

  if (row.target === null) {
    return (
      <span className="lq-muted" title={t('scoring.owner.noneHint')}>
        {t('scoring.owner.none')}
      </span>
    );
  }
  return (
    <span>
      <span className="lq-scores__owner">{row.target.owner}</span>
      <span className="lq-scores__sub">{t('scoring.owner.viaContract')}</span>
    </span>
  );
}
